import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  CampoDeSelecao,
  Cartao,
  CelulaDeTotal,
  EstadoVazio,
  Selo,
  ROTULO_STATUS_CONTRATO,
  SeloStatusContrato,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  type ColunaTabela,
  type StatusContrato,
  type TomSemantico,
} from '../../componentes/ui';
import { EnvioDeSped } from '../../componentes/EnvioDeSped';
import { LancamentoDeValores } from '../../componentes/LancamentoDeValores';
import { CronogramaDeEnvios } from './CronogramaDeEnvios';
import { useCronograma, useSpeds } from '../../hooks/useOperacao';
import type { ContratoResumo, LinhaCronograma, LinhaSped } from '../../servicos/operacao';
import { useExercicioVigente } from '../../servicos/exercicio';
import { formatarCompetencia } from '../../servicos/formato';
import { ModalDeEnvioDoCalculo, colunasDoCalculo } from './EnvioDoCalculo';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import type { ColunaDaPlanilha } from '../../servicos/planilha';
import { TransicaoDoContrato } from './TransicaoDoContrato';
import { ModalDeDesagioDoAno, ModalDeEdicaoDoCalculo } from './EdicaoDoCalculo';
import { baixarArquivo, requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';

/**
 * Aba de operação de crédito — o coração da tela do cliente.
 *
 * Três painéis, os mesmos do Deca 1: **Relatórios** (o resultado por mês),
 * **Arquivos** (os SPEDs que alimentaram a conta) e **Cronograma de envios**
 * (o que o sistema vai disparar e quando).
 *
 * A ordem não é arbitrária: é a mesma da operação. Chega o arquivo, sai o
 * cálculo, dispara o e-mail. Quem abre a tela procurando "por que este mês não
 * foi cobrado" percorre os três nessa sequência.
 */

type Painel = 'relatorios' | 'arquivos' | 'cronograma';

/**
 * Quem olha a aba, e o que ela oferece:
 *
 * - `operacao` — o admin: tudo, como sempre foi;
 * - `gestao` — quem gerencia o cliente sem ser a operação (o assessor dele):
 *   cálculo, arquivos, deságio, e-mail do cálculo e o **passo dele** no
 *   contrato. O cronograma de e-mails da operação fica só para ver;
 * - `cliente` — o gestor do próprio cliente: envia SPED, lança valores, manda
 *   o cálculo por e-mail e dá o passo dele no contrato. Deságio e valores do
 *   mês são o preço da operação — ele vê, não altera (a API recusa);
 * - `leitura` — quem acompanha (o fornecedor, um colaborador): vê tudo e não
 *   aciona nada.
 *
 * A API confere cada ação de novo; o modo é para a tela não oferecer o que
 * seria recusado.
 */
export type ModoDaAba = 'operacao' | 'gestao' | 'cliente' | 'leitura';

export function AbaIcmsRecorrente(props: {
  readonly clienteId: string;
  readonly contratos: readonly ContratoResumo[];
  readonly modo?: ModoDaAba;
}): JSX.Element {
  const modo = props.modo ?? 'operacao';
  const [painel, setPainel] = useState<Painel>('relatorios');
  const [envioAberto, setEnvioAberto] = useState(false);
  const [lancamentoAberto, setLancamentoAberto] = useState(false);
  const [emailAberto, setEmailAberto] = useState(false);

  const speds = useSpeds(props.clienteId);
  const cronograma = useCronograma(props.clienteId);

  const todasAsLinhas = speds.data ?? [];

  /**
   * Anos disponíveis no seletor.
   *
   * Vêm dos contratos **e** dos cálculos. Só dos contratos, um cliente que já
   * enviou SPED mas ainda não fechou contrato ficaria com o seletor vazio — e a
   * tela mostraria os cálculos sem deixar filtrar.
   *
   * A competência de um SPED alimenta a operação do ano **seguinte** (RN-619),
   * então um arquivo de 2025 entra como ano de operação 2026.
   */
  const anos = [
    ...new Set([
      ...props.contratos.map((c) => c.ano),
      ...todasAsLinhas.map((l) => Number(l.competencia.slice(0, 4)) + 1),
    ]),
  ].sort((a, b) => b - a);

  const [anoEscolhido, setAno] = useState<number | null>(null);
  // Enquanto o SPED não chega, `anos` está vazio; o primeiro ano só existe
  // depois da resposta, e por isso a escolha inicial é derivada, não guardada.
  // RN-620 — abre no exercício vigente quando o cliente tem dado nele; senão,
  // no mais recente que tiver. Um cliente com SPED de 2026 abre em 2027.
  const vigente = useExercicioVigente();
  const ano = anoEscolhido ?? (anos.includes(vigente) ? vigente : (anos[0] ?? null));
  const anoDoSped = ano === null ? null : ano - 1;

  const doAno = props.contratos.filter((c) => c.ano === ano);
  // O status do ano é o do contrato mais recente dele — um cliente pode ter
  // vários fornecedores no mesmo ano (RN-149), e eles avançam separadamente.

  /**
   * O filtro que faltava.
   *
   * Sem ele a tela mostrava as 19 competências de um cliente com arquivos de
   * três anos, qualquer que fosse o ano selecionado — e os totais no topo
   * somavam os três, o que faz o "ICMS apurado" do exercício não bater com nada.
   */
  const linhas =
    anoDoSped === null
      ? todasAsLinhas
      : todasAsLinhas.filter((l) => l.competencia.startsWith(String(anoDoSped)));
  const totais = linhas.reduce(
    (a, l) => ({
      /**
       * Mês negativo entra como **zero** no total do ICMS apurado.
       *
       * Apuração negativa não é dívida: é saldo credor vindo do mês anterior.
       * Não há o que compensar, e o mês já produz crédito, benefício e valor a
       * pagar zerados (RN-075). Somar o negativo subtraía dos meses que de fato
       * geraram crédito — a tela mostrava R$ 57.484,04 de crédito ao lado de um
       * apurado de −R$ 15.162,75, número que não descreve nada.
       *
       * A mesma regra vale na calculadora pública, onde a soma é do servidor.
       * Aqui os totais são do navegador, então a regra precisa existir nos dois.
       */
      totalSped: a.totalSped + Math.max(l.totalSped, 0),
      valueSped: a.valueSped + l.valueSped,
      valueBeneficio: a.valueBeneficio + (l.valueBeneficio ?? 0),
      total: a.total + (l.total ?? 0),
    }),
    { totalSped: 0, valueSped: 0, valueBeneficio: 0, total: 0 },
  );

  return (
    <div className="flex flex-col gap-6">
      {/* ── Contrato do ano ── */}
      <Cartao como="section" titulo="Contrato do exercício">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            {/*
              Aqui só a **contagem**. A situação fica em `TransicaoDoContrato`,
              logo abaixo, e num lugar só.

              Havia dois selos na mesma tela com regras opostas: este pegava o
              status "mais avançado" do ano e o de baixo, a regra do domínio
              (`selecionarContratoAtual`, RN-030), que escolhe o primeiro **não**
              fechado. Num cliente com 13 contratos em 2026, um deles aberto,
              este dizia "Fechado" e o outro "Aberto" — a poucos centímetros de
              distância. Quem manda é a regra do domínio: é ela que decide se a
              transição é possível.
            */}
            {doAno.length === 0 ? (
              <p className="text-sm text-conteudo-secundario">
                Nenhum contrato registrado {ano === null ? '' : `em ${ano}`}.
              </p>
            ) : (
              <p className="text-sm text-conteudo-secundario">
                {doAno.length} contrato(s) no ano
                {doAno.length > 1 && ', um por fornecedor'}
              </p>
            )}
          </div>

          {anos.length > 0 && (
            <CampoDeSelecao
              rotulo="Ano da operação"
              className="w-44"
              ajuda={anoDoSped === null ? undefined : `Apurado sobre o SPED de ${anoDoSped}.`}
              value={String(ano ?? '')}
              onChange={(e) => setAno(Number(e.target.value))}
              opcoes={anos.map((a) => ({ valor: String(a), rotulo: String(a) }))}
            />
          )}
        </div>

        {/*
          As ações de contrato ficam abaixo do filtro de ano, e não como um botão
          grande no topo ao lado dele: no Deca 1 "Fechar contrato" era vizinho do
          seletor de exercício, a um clique de quem só queria trocar de ano — e a
          ação materializa o deságio de doze meses.
        */}
        {ano !== null && <TransicaoDoContrato clienteId={props.clienteId} ano={ano} modo={modo} />}

        {modo === 'operacao' && doAno.length > 0 && ano !== null && (
          <ReenvioDaRecepcao clienteId={props.clienteId} ano={ano} />
        )}
      </Cartao>

      {/* ── Indicadores ── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Indicador
          rotulo="ICMS apurado"
          valor={totais.totalSped}
          detalhe={`${linhas.length} competência(s)`}
        />
        <Indicador rotulo="Crédito utilizável" valor={totais.valueSped} />
        {/* O que se paga vem antes do que se economiza, como na tabela abaixo. */}
        <Indicador rotulo="Valor a ser pago" valor={totais.total} />
        <Indicador rotulo="Benefício econômico" valor={totais.valueBeneficio} economia />
      </div>

      {/* ── Painéis ── */}
      <div role="group" aria-label="Painéis da operação" className="flex flex-wrap gap-2">
        <BotaoDePainel ativo={painel === 'relatorios'} aoClicar={() => setPainel('relatorios')}>
          Relatórios
        </BotaoDePainel>
        <BotaoDePainel ativo={painel === 'arquivos'} aoClicar={() => setPainel('arquivos')}>
          Arquivos <Contagem n={linhas.length} />
        </BotaoDePainel>
        <BotaoDePainel ativo={painel === 'cronograma'} aoClicar={() => setPainel('cronograma')}>
          Cronograma de envios <Contagem n={cronograma.data?.length ?? 0} />
        </BotaoDePainel>
      </div>

      {painel === 'relatorios' && (
        <Relatorios
          linhas={linhas}
          contratos={doAno}
          totais={totais}
          carregando={speds.isLoading}
          planilha={{
            titulo: ano === null ? 'Cálculo ICMS Recorrente' : `Cálculo ICMS Recorrente ${ano}`,
            colunas: colunasDoCalculo((l) => {
              const status = statusDoMes(l, doAno);
              const rotulo = status === null ? 'Sem contrato' : ROTULO_STATUS_CONTRATO[status];
              return l.statusEnvio === 'ENVIADO' ? `${rotulo} (aproveitado)` : rotulo;
            }),
          }}
          {...(modo === 'leitura' ? {} : { aoEnviarEmail: () => setEmailAberto(true) })}
        />
      )}
      {painel === 'arquivos' && (
        <Arquivos
          linhas={linhas}
          carregando={speds.isLoading}
          aoEnviar={() => setEnvioAberto(true)}
          aoLancar={() => setLancamentoAberto(true)}
          clienteId={props.clienteId}
          ano={ano}
          somenteLeitura={modo === 'leitura'}
          podeAlterarCalculo={modo === 'operacao' || modo === 'gestao'}
        />
      )}
      {painel === 'cronograma' && (
        <CronogramaDeEnvios
          clienteId={props.clienteId}
          linhas={cronograma.data ?? []}
          carregando={cronograma.isLoading}
          {...(ano === null ? {} : { ano })}
          somenteLeitura={modo !== 'operacao'}
        />
      )}

      <EnvioDeSped
        clienteId={props.clienteId}
        aberto={envioAberto}
        aoFechar={() => setEnvioAberto(false)}
      />

      {/* RN-085 — a apuração também entra pelo valor, sem arquivo. */}
      <LancamentoDeValores
        clienteId={props.clienteId}
        aberto={lancamentoAberto}
        aoFechar={() => setLancamentoAberto(false)}
        {...(ano === null ? {} : { anoDaOperacao: ano })}
      />

      <ModalDeEnvioDoCalculo
        clienteId={props.clienteId}
        ano={ano ?? new Date().getUTCFullYear()}
        aberto={emailAberto}
        aoFechar={() => setEmailAberto(false)}
      />
    </div>
  );
}

/* ─── Relatórios ──────────────────────────────────────────────────────────── */

/**
 * As colunas são as do Deca 1, com dois nomes trocados, e uma a mais.
 *
 * A coluna **Fornecedor** não existia no Deca 1: num split de fornecedor
 * (RN-021) a grade mostrava valores de dois fornecedores diferentes sem dizer
 * qual mês é de quem — e a pergunta "por que março tem outro deságio" só se
 * respondia abrindo o cronograma.
 *
 * "Limite de Utilização" virou "% utilização" e "% Perc. de Deságio" virou
 * "% deságio": os originais misturavam a unidade no rótulo ("Perc." e "%" na
 * mesma frase) e "limite" sugeria um teto, quando o número é o percentual da
 * faixa que de fato foi aplicado.
 */
/**
 * A situação do contrato que atende o mês — a regra do Deca 1.
 *
 * Um cliente pode ter mais de um contrato no ano (RN-149): o status da linha
 * é o do contrato do **fornecedor daquele mês** (RN-151). Sem fornecedor na
 * linha, vale o contrato do ano que ainda não fechou (o que tem trabalho); sem
 * contrato nenhum, `null`. Mostrava "A aproveitar" aqui — o aproveitamento do
 * mês é outra informação, e virou o selo secundário.
 */
function statusDoMes(l: LinhaSped, contratos: readonly ContratoResumo[]): StatusContrato | null {
  const doFornecedor =
    l.fornecedorId === null ? undefined : contratos.find((c) => c.fornecedorId === l.fornecedorId);
  const contrato =
    doFornecedor ?? contratos.find((c) => c.status !== 'FECHADO') ?? contratos[0] ?? null;
  return contrato === null ? null : (contrato.status as StatusContrato);
}

function colunasDoRelatorio(
  contratos: readonly ContratoResumo[],
  linhas: readonly LinhaSped[],
): readonly ColunaTabela<LinhaSped>[] {
  return [
    {
      chave: 'competencia',
      titulo: 'Mês',
      larguraMinima: '7rem',
      celula: (l) => formatarCompetencia(l.competencia),
    },
    {
      chave: 'status',
      titulo: 'Status',
      larguraMinima: '11rem',
      celula: (l) => {
        const status = statusDoMes(l, contratos);
        return (
          <span className="flex flex-wrap items-center gap-1.5">
            {status === null ? (
              <Selo tom="neutro">Sem contrato</Selo>
            ) : (
              <SeloStatusContrato status={status} />
            )}
            {l.statusEnvio === 'ENVIADO' && (
              <Selo tom="positivo" tamanho="pequeno">
                Aproveitado
              </Selo>
            )}
          </span>
        );
      },
    },
    colunaDoFornecedor(linhas),
    ...COLUNAS_DO_CALCULO,
  ];
}

/**
 * A largura da coluna do fornecedor acompanha o conteúdo.
 *
 * Eram 13rem fixos: sem fornecedor em nenhum mês, a coluna era uma faixa larga
 * de "—" empurrando "Valor a ser pago" para fora da tela. Agora, vazia, ela
 * fica do tamanho do título; com nome, cresce até caber o maior numa linha,
 * com teto de 15rem — nome mais longo quebra em duas linhas em vez de esconder
 * os valores na rolagem.
 */
function larguraDoFornecedor(linhas: readonly LinhaSped[]): string | undefined {
  const maior = Math.max(0, ...linhas.map((l) => l.fornecedorNome?.length ?? 0));
  if (maior === 0) return undefined;
  // Em text-sm, maiúscula ocupa ~0,55rem por caractere; 2rem é o px-4 da célula.
  const rem = Math.ceil(maior * 0.55) + 2;
  return `${Math.min(15, Math.max(8, rem))}rem`;
}

function colunaDoFornecedor(linhas: readonly LinhaSped[]): ColunaTabela<LinhaSped> {
  const largura = larguraDoFornecedor(linhas);
  return {
    chave: 'fornecedor',
    titulo: 'Fornecedor',
    ...(largura === undefined ? {} : { larguraMinima: largura }),
    celula: (l) => {
      const nome = l.fornecedorNome;
      return nome === null || nome === '' ? (
        // Sem fornecedor ainda: o cliente não fechou, ou fechou sem escolher
        // (RN-019) e o assessor ainda não definiu.
        <span className="text-conteudo-sutil" aria-label="Sem fornecedor definido">
          —
        </span>
      ) : (
        nome
      );
    },
  };
}

const COLUNAS_DO_CALCULO: readonly ColunaTabela<LinhaSped>[] = [
  {
    chave: 'totalSped',
    titulo: 'ICMS apurado',
    numerica: true,
    celula: (l) => <ValorFiscal valor={l.totalSped} />,
  },
  {
    chave: 'percentSped',
    titulo: '% utilização',
    numerica: true,
    celula: (l) => <ValorPercentual percentual={l.percentSpedPercentual} />,
  },
  {
    chave: 'valueSped',
    titulo: 'Valor de utilização',
    numerica: true,
    celula: (l) => <ValorFiscal valor={l.valueSped} />,
  },
  {
    chave: 'percentBeneficio',
    titulo: '% deságio',
    numerica: true,
    celula: (l) =>
      l.percentBeneficioPercentual === null ? (
        <span aria-hidden="true">—</span>
      ) : (
        <ValorPercentual percentual={l.percentBeneficioPercentual} />
      ),
  },
  {
    chave: 'total',
    titulo: 'Valor a ser pago',
    numerica: true,
    celula: (l) =>
      l.total === null ? <span aria-hidden="true">—</span> : <ValorFiscal valor={l.total} />,
  },
  {
    chave: 'valueBeneficio',
    titulo: 'Ben. econômico',
    numerica: true,
    celula: (l) =>
      l.valueBeneficio === null ? (
        <span aria-hidden="true">—</span>
      ) : (
        <ValorFiscal valor={l.valueBeneficio} variante="economia" />
      ),
  },
];

function Relatorios(props: {
  readonly linhas: readonly LinhaSped[];
  readonly contratos: readonly ContratoResumo[];
  readonly totais: { totalSped: number; valueSped: number; valueBeneficio: number; total: number };
  readonly carregando: boolean;
  /** O "Extrair xlsx": as linhas são as da tabela. */
  readonly planilha: {
    readonly titulo: string;
    readonly colunas: readonly ColunaDaPlanilha<LinhaSped>[];
  };
  /** Ausente = quem olha não manda o cálculo por e-mail (só acompanha). */
  readonly aoEnviarEmail?: () => void;
}): JSX.Element {
  if (!props.carregando && props.linhas.length === 0) {
    return (
      <EstadoVazio
        titulo="Nenhum resultado apurado"
        descricao="Envie os arquivos de SPED para o sistema calcular o aproveitamento por competência."
      />
    );
  }

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight">
          Apresentação de resultado até o período
        </h2>
        <div className="flex flex-wrap gap-3">
          <BotaoExtrairXlsx
            titulo={props.planilha.titulo}
            colunas={props.planilha.colunas}
            linhas={props.linhas}
          />
          {props.aoEnviarEmail !== undefined && (
            <Botao onClick={props.aoEnviarEmail}>Enviar e-mail</Botao>
          )}
        </div>
      </div>

      <Tabela
        legenda="Resultado por competência"
        densidade="compacta"
        colunas={colunasDoRelatorio(props.contratos, props.linhas)}
        dados={props.linhas}
        chaveDaLinha={(l) => l.id}
        chaveDeCabecalhoDaLinha="competencia"
        carregando={props.carregando}
        rodape={
          <tr>
            {/* Mês, Status e Fornecedor: três colunas sem total. */}
            <CelulaDeTotal colSpan={3} cabecalhoDaLinha>
              Total
            </CelulaDeTotal>
            <CelulaDeTotal numerica>
              <ValorFiscal valor={props.totais.totalSped} />
            </CelulaDeTotal>
            <CelulaDeTotal numerica>
              <span aria-hidden="true">—</span>
            </CelulaDeTotal>
            <CelulaDeTotal numerica>
              <ValorFiscal valor={props.totais.valueSped} />
            </CelulaDeTotal>
            <CelulaDeTotal numerica>
              <span aria-hidden="true">—</span>
            </CelulaDeTotal>
            <CelulaDeTotal numerica>
              <ValorFiscal valor={props.totais.total} />
            </CelulaDeTotal>
            <CelulaDeTotal numerica>
              <ValorFiscal valor={props.totais.valueBeneficio} variante="economia" />
            </CelulaDeTotal>
          </tr>
        }
      />
    </section>
  );
}

/* ─── Arquivos ────────────────────────────────────────────────────────────── */

function Arquivos(props: {
  readonly linhas: readonly LinhaSped[];
  readonly carregando: boolean;
  readonly aoEnviar: () => void;
  /** RN-085 — lançar as competências pelo valor, quando não há arquivo. */
  readonly aoLancar: () => void;
  readonly clienteId: string;
  readonly ano: number | null;
  /** Quem só acompanha baixa o arquivo, mas não edita, não lança, não envia. */
  readonly somenteLeitura?: boolean;
  /**
   * Editar o mês e o deságio do ano: a operação e o assessor. O próprio
   * cliente envia e lança, mas não mexe no preço.
   */
  readonly podeAlterarCalculo?: boolean;
}): JSX.Element {
  const leitura = props.somenteLeitura === true;
  const alteraCalculo = !leitura && props.podeAlterarCalculo !== false;
  const [editando, setEditando] = useState<LinhaSped | null>(null);
  const [desagioAberto, setDesagioAberto] = useState(false);

  const colunas: readonly ColunaTabela<LinhaSped>[] = [
    {
      chave: 'competencia',
      titulo: 'Competência',
      larguraMinima: '9rem',
      celula: (l) => formatarCompetencia(l.competencia),
    },
    {
      chave: 'regime',
      titulo: 'Regime',
      larguraMinima: '8rem',
      celula: (l) => (l.industrial ? 'Industrial' : 'Comercial'),
    },
    {
      chave: 'totalSped',
      titulo: 'ICMS apurado no arquivo',
      numerica: true,
      celula: (l) => <ValorFiscal valor={l.totalSped} />,
    },
    {
      chave: 'statusEnvio',
      titulo: 'Transferência',
      larguraMinima: '10rem',
      celula: (l) => (
        <Selo tom={l.statusEnvio === 'ENVIADO' ? 'positivo' : 'neutro'}>
          {l.statusEnvio === 'ENVIADO' ? 'Confirmada' : 'Pendente'}
        </Selo>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '13rem',
      celula: (l) => (
        <span className="flex flex-wrap justify-end gap-2">
          <Botao
            variante="sutil"
            tamanho="pequeno"
            onClick={() => {
              void baixarArquivo(`/icms/speds/${l.id}/arquivo`, `sped-${l.competencia}.txt`);
            }}
          >
            Baixar SPED
          </Botao>
          {alteraCalculo && (
            <Botao variante="sutil" tamanho="pequeno" onClick={() => setEditando(l)}>
              Editar
            </Botao>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">Arquivos de SPED</h2>
          <p className="mt-1 text-sm text-conteudo-secundario">
            Um por competência. O cálculo do mês nasce do arquivo — reenviar substitui a apuração.
            Sem o arquivo em mãos, "Lançar valores" calcula pelo ICMS apurado que o contador
            informou.
          </p>
        </div>
        {!leitura && (
          <span className="flex flex-wrap gap-2">
            {alteraCalculo && props.ano !== null && props.linhas.length > 0 && (
              <Botao variante="secundario" onClick={() => setDesagioAberto(true)}>
                Deságio do ano
              </Botao>
            )}
            <Botao variante="secundario" onClick={props.aoLancar}>
              Lançar valores
            </Botao>
            <Botao onClick={props.aoEnviar}>Enviar SPED</Botao>
          </span>
        )}
      </div>

      {!props.carregando && props.linhas.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum arquivo enviado"
          descricao="Este cliente ainda não teve arquivos de SPED processados."
        />
      ) : (
        <Tabela
          legenda="Arquivos de SPED por competência"
          colunas={colunas}
          dados={props.linhas}
          chaveDaLinha={(l) => l.id}
          chaveDeCabecalhoDaLinha="competencia"
          carregando={props.carregando}
        />
      )}

      <ModalDeEdicaoDoCalculo
        clienteId={props.clienteId}
        linha={editando}
        aoFechar={() => setEditando(null)}
      />

      {props.ano !== null && (
        <ModalDeDesagioDoAno
          clienteId={props.clienteId}
          ano={props.ano}
          aberto={desagioAberto}
          aoFechar={() => setDesagioAberto(false)}
        />
      )}
    </section>
  );
}

/* ─── Peças ───────────────────────────────────────────────────────────────── */

function Indicador(props: {
  readonly rotulo: string;
  readonly valor: number;
  readonly detalhe?: string;
  readonly economia?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-painel border border-borda bg-superficie-elevada p-5 shadow-cartao">
      <p className="text-xs font-medium uppercase tracking-wide text-conteudo-secundario">
        {props.rotulo}
      </p>
      <p className="mt-2">
        <ValorFiscal
          valor={props.valor}
          tamanho="grande"
          {...(props.economia === true ? { variante: 'economia' as const } : {})}
        />
      </p>
      {props.detalhe !== undefined && (
        <p className="mt-1 text-xs text-conteudo-sutil">{props.detalhe}</p>
      )}
    </div>
  );
}

function BotaoDePainel(props: {
  readonly ativo: boolean;
  readonly aoClicar: () => void;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={props.ativo}
      onClick={props.aoClicar}
      className={`inline-flex h-9 items-center gap-2 rounded-full border px-4 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano ${
        props.ativo
          ? 'border-transparent bg-superficie-vidro font-medium text-conteudo-primario'
          : 'border-transparent text-conteudo-secundario hover:bg-superficie-vidro/60 hover:text-conteudo-primario'
      }`}
    >
      {props.children}
    </button>
  );
}

function Contagem(props: { readonly n: number }): JSX.Element {
  return <span className="text-xs text-conteudo-sutil">{props.n}</span>;
}

/** `CLIENTE_APROVA_CREDITO` → `Cliente aprova credito`. */
function humanizar(chave: string): string {
  const t = chave.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function tomDoStatus(status: string): TomSemantico {
  if (status === 'ENVIADO' || status === 'ENVIADO_MANUAL' || status === 'CONCLUIDO')
    return 'positivo';
  if (status === 'FALHOU') return 'critico';
  if (status === 'AGENDADO') return 'progresso';
  return 'neutro';
}

/* ─── Reenvio do e-mail de recepção ───────────────────────────────────────── */

/**
 * RN-028 — o e-mail de "recepção no sistema", reenviado à mão.
 *
 * Ele sai sozinho quando o contrato fecha. Este botão existe para quando não
 * chegou: endereço errado no cadastro, caixa cheia, contato trocado. Não mexe no
 * estado do contrato — por isso fica discreto, e não ao lado de "Fechar
 * contrato", que é irreversível.
 */
function ReenvioDaRecepcao(props: {
  readonly clienteId: string;
  readonly ano: number;
}): JSX.Element {
  const reenviar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<{ destinatarios: string[]; ensaio?: boolean }>(
        `/icms/contrato/${props.clienteId}/email-recepcao`,
        { metodo: 'POST', corpo: { ano: props.ano } },
      ),
  });

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <Botao
        variante="sutil"
        tamanho="pequeno"
        carregando={reenviar.isPending}
        onClick={() => reenviar.mutate()}
      >
        Reenviar e-mail de recepção
      </Botao>
      <span className="text-xs text-conteudo-sutil">
        O mesmo aviso que sai no fechamento do contrato, para quando ele não chegou.
      </span>

      {reenviar.isSuccess && (
        <Aviso
          tom={reenviar.data.ensaio === true ? 'informacao' : 'positivo'}
          titulo={reenviar.data.ensaio === true ? 'Modo ensaio — não saiu' : 'E-mail reenviado'}
          className="w-full"
        >
          {reenviar.data.destinatarios.join(', ')}
        </Aviso>
      )}
      {reenviar.isError && <ErroDaApi titulo="Não foi possível reenviar" erro={reenviar.error} />}
    </div>
  );
}
